import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-monk-blessing-village.jpg";

const itinerary = [
  {
    time: "Stop 1 · Pagoda",
    title: "Monk Blessing Ceremony",
    body:
      "Welcomed by your local guide at your accommodation, you'll travel to a serene Buddhist monastery. At the pagoda, gain a deeper understanding of Buddhism while interacting with monks and witnessing their sacred chanting during the blessing ritual.",
  },
  {
    time: "Stop 2 · Chreav Commune",
    title: "Khmer Village Walk",
    body:
      "Walk through traditional stilt houses and lush green pathways, observe villagers tending crops and vegetables, and experience the rhythm of authentic Cambodian life. Your community guide shares insights into Khmer culture, customs and local livelihoods.",
  },
  {
    time: "Stop 3 · Local Market",
    title: "Fresh Produce & Daily Life",
    body:
      "Visit a small local market where farmers sell fresh vegetables, fish and tropical fruits an immersive glimpse into rural Cambodian life and traditional commerce.",
  },
  {
    time: "Stop 4 · Rice Spirit Producer",
    title: "Tasting & Khmer Snacks",
    body:
      "Visit a homemade rice spirit producer and observe the careful process of crafting this traditional drink. Taste a variety of fermented spirits and enjoy fresh fruits, Khmer pastries and local snacks before returning to your hotel.",
  },
];

const includes = [
  "All entrance fees",
  "Professional English-speaking guide",
  "Air-conditioned transportation",
  "Complimentary bottled water & cold towels",
];

const excludes = [
  "Tips and gratuities",
  "Personal expenses",
  "Travel insurance",
];

const essentials = [
  "Modest clothing for temples",
  "Comfortable walking shoes",
  "Light rain jacket",
  "Sun protection & insect repellent",
];

const TourMonkBlessingVillage = () => {
  return (
    <main className="bg-background">
      {/* HERO */}
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Khmer village and monk blessing private tour near Siem Reap with French-speaking guide, authentic Cambodia cultural experience"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Half-Day Cultural Experience</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Khmer Village Tour <em className="font-light">with</em> Monk Blessing.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              A half-day immersion into the spiritual heart of Cambodia a sacred blessing ceremony at a quiet monastery, a walk through Chreav Commune's stilt-house village, and a tasting of homemade rice spirits with Khmer snacks.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 0.5 Day</span>
              <span>Group · 1–12</span>
              <span>Pickup · 7:30 AM / 8:00 AM / 8:30 AM / 2:00 PM</span>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Blessings, village life, and a taste of rice spirit.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              You will be welcomed by our local guide at your accommodation and accompanied to a serene Buddhist monastery for a monk blessing ceremony. At the pagoda, you will gain a deeper understanding of Buddhism and its practices while interacting with monks and witnessing their sacred chanting during the blessing ritual.
            </p>
            <p>
              Next, we journey to Chreav Commune, where you will explore a lush, green village full of daily life. Walk through traditional stilt houses, observe villagers tending their crops and vegetables, and experience the rhythm of authentic Cambodian village life. Our community guide will share insights into Khmer culture, customs and traditions, as well as local livelihoods such as farming and fishing.
            </p>
            <p>
              Following the village walk, we visit a local market where farmers sell fresh vegetables, fish and fruits offering an immersive glimpse into rural Cambodian life and traditional commerce. Finally, we visit a homemade rice spirit producer, where you can observe the careful process of crafting this traditional drink. After the demonstration, you will have the opportunity to taste a variety of fermented spirits and enjoy fresh fruits, Khmer pastries and local snacks before returning to your hotel.
            </p>
          </div>
        </div>
      </section>

      {/* ITINERARY */}
      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                A half-day, four stops.
              </h2>
              <ol className="space-y-px">
                {itinerary.map((s, idx) => (
                  <li
                    key={idx}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
                  >
                    <div className="flex flex-col items-start gap-y-2 text-base">
                      <span className="font-display text-primary tabular-nums text-xl">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                        {s.time}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                        {s.title}
                      </h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar pricePerPerson={95} tourTitle="Khmer Village Tour with Monk Blessing Experience" pricingTiers={[{ maxPax: 1, price: 95 }, { maxPax: 2, price: 145 }, { maxPax: 3, price: 178 }, { maxPax: 4, price: 216 }, { maxPax: 6, price: 226 }, { maxPax: 7, price: 256 }, { maxPax: 8, price: 286 }, { maxPax: 9, price: 310 }, { maxPax: 10, price: 326 }]} />
          </div>
        </div>
      </section>

      {/* INCLUDES / EXCLUDES */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Everything you need for the morning.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Essential Items</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                {essentials.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Varied climate across regions. Hot in lowlands, cooler in Mondulkiri highlands. Coastal breezes near islands.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Diverse regional cuisine. Fresh seafood on the coast, Kampot pepper specialties, and traditional Khmer dishes throughout.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Additional Information</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                <li>• Pickup time: 7:00 AM – 8:00 AM (confirmed one day before)</li>
                <li>• Dress code: Casual but respectful in temples</li>
                <li>• Children under 5 years old are not permitted</li>
                <li>• Maximum group size: 1–12 travellers</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourMonkBlessingVillage;
